import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import Sidebar from "./Sidebar";
import "./Dashboard.css";
import "./AIResumeBuilder.css";
import "./JobTailoredResumeForm.css";
import Footer from "../Landing Page/Footer";
import Feedback from "./Feedback";
import { useSubscription } from "../SubscriptionContext";
import { useAuth } from "../AuthContext";
// eslint-disable-next-line no-unused-vars
import axios from "axios";
// eslint-disable-next-line no-unused-vars
import { apiUrl } from "../config/apiBase";
import ResumePreview from "./ResumePreview";
import JobTailoredResumeForm from "./JobTailoredResumeForm";
import html2pdf from 'html2pdf.js';

function JobTailoredResume() {
  const { canCreateTailoredResume, getResumeUsageInfo } = useSubscription();
  const { refreshTokenIfNeeded } = useAuth();
  const location = useLocation();
  const [jobUrl, setJobUrl] = useState("");
  const [resumeFile, setResumeFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [tailoredData, setTailoredData] = useState(null);
  const [animatedProfessionalSummary, setAnimatedProfessionalSummary] = useState("");
  const [resumeId, setResumeId] = useState(null);
  const [language, setLanguage] = useState("english");

  // Form data states (same as AIResumeBuilder)
  const [personalInfo, setPersonalInfo] = useState({
    fullName: "",
    email: "",
    phone: "",
    location: "",
    linkedIn: "",
    portfolio: ""
  });

  const [professionalSummary, setProfessionalSummary] = useState("");
  const [experience, setExperience] = useState([
    { type: 'work', company: "", position: "", startDate: "", endDate: "", current: false, description: "", location: "", projectName: "", techStack: "", projectLink: "" }
  ]);
  const [education, setEducation] = useState([
    { school: "", degree: "", field: "", graduationYear: "", startYear: "", gpa: "", location: "" }
  ]);
  const [skills, setSkills] = useState("");

  // Language skills state - Default to English
  const [languageSkills, setLanguageSkills] = useState([
    { language: "English", proficiency: "Native" }
  ]);

  // Store original data for undo functionality
  const [originalPersonalInfo, setOriginalPersonalInfo] = useState({
    fullName: "",
    email: "",
    phone: "",
    location: "",
    linkedIn: "",
    portfolio: ""
  });
  const [originalProfessionalSummary, setOriginalProfessionalSummary] = useState("");
  const [originalExperience, setOriginalExperience] = useState([
    { type: 'work', company: "", position: "", startDate: "", endDate: "", current: false, description: "", location: "", projectName: "", techStack: "", projectLink: "" }
  ]);
  const [originalEducation, setOriginalEducation] = useState([
    { school: "", degree: "", field: "", graduationYear: "", startYear: "", gpa: "", location: "" }
  ]);
  const [originalSkills, setOriginalSkills] = useState("");

  // PDF generation states
  const [pdfGenerating, setPdfGenerating] = useState(false);

  // Validation states
  const [validationErrors, setValidationErrors] = useState({});

  // AI assistance states
  const [aiLoading, setAiLoading] = useState(false);
  const [aiFieldType, setAiFieldType] = useState(null);

  // Session conflict detection states
  const [showSessionConflictModal, setShowSessionConflictModal] = useState(false);
  const [hasIncompleteSession, setHasIncompleteSession] = useState(false);

  // Load resumeId from sessionStorage on component mount - only if continuing a session
  useEffect(() => {
    // Check if this is a fresh navigation (not continuing a session)
    const continueSession = sessionStorage.getItem('jobTailoredContinueSession');
    const referrer = document.referrer;
    const isFromDifferentPage = referrer && !referrer.includes('/dashboard/job-tailored-resume');
    const isFreshNavigation = location.state?.freshNavigation === true || !continueSession || isFromDifferentPage;

    // Check if we have state from the form page
    const formData = sessionStorage.getItem('jobTailoredFormData');
    const resumeFileData = sessionStorage.getItem('jobTailoredResumeFile');
    
    if (formData && resumeFileData) {
      // Received form data from previous session
      const parsedData = JSON.parse(formData);
      setJobUrl(parsedData.jobUrl);
      
      // Convert base64 back to File object
      const base64Data = resumeFileData.split(',')[1];
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: parsedData.resumeFileType });
      const file = new File([blob], parsedData.resumeFileName, { type: parsedData.resumeFileType });
      setResumeFile(file);
      
      // Clear the sessionStorage to prevent re-initialization
      sessionStorage.removeItem('jobTailoredFormData');
      sessionStorage.removeItem('jobTailoredResumeFile');

      // Note: handleSubmit will be called when the form is submitted manually
      return;
    }

    // Check for incomplete session
    const savedTailoredData = sessionStorage.getItem('jobTailoredData');
    const savedCurrentStep = sessionStorage.getItem('jobTailoredCurrentStep');

    if (continueSession === 'true' && savedTailoredData && !isFreshNavigation) {
      const parsedTailoredData = JSON.parse(savedTailoredData);
      const parsedCurrentStep = parseInt(savedCurrentStep) || 0;

      // If there's an incomplete session (has data but not finished step 4), show conflict modal
      if (parsedTailoredData && parsedCurrentStep < 4) {
        setHasIncompleteSession(true);
        setShowSessionConflictModal(true);
        return; // Don't load the session yet, wait for user choice
      }
    }

    // If no incomplete session or user chose to continue, load session data
    if (continueSession === 'true' && !isFreshNavigation) {
      // Load saved session data
      const savedResumeId = sessionStorage.getItem('jobTailoredResumeId');
      const savedTailoredData = sessionStorage.getItem('jobTailoredData');
      const savedCurrentStep = sessionStorage.getItem('jobTailoredCurrentStep');
      const savedPersonalInfo = sessionStorage.getItem('jobTailoredPersonalInfo');
      const savedProfessionalSummary = sessionStorage.getItem('jobTailoredProfessionalSummary');
      const savedExperience = sessionStorage.getItem('jobTailoredExperience');
      const savedEducation = sessionStorage.getItem('jobTailoredEducation');
      const savedSkills = sessionStorage.getItem('jobTailoredSkills');

      if (savedResumeId) {
        setResumeId(savedResumeId);
      }
      if (savedTailoredData) {
        setTailoredData(JSON.parse(savedTailoredData));
      }
      if (savedCurrentStep) {
        setCurrentStep(parseInt(savedCurrentStep));
      }
      if (savedPersonalInfo) {
        setPersonalInfo(JSON.parse(savedPersonalInfo));
        setOriginalPersonalInfo(JSON.parse(savedPersonalInfo));
      }
      if (savedProfessionalSummary) {
        setProfessionalSummary(savedProfessionalSummary);
        setOriginalProfessionalSummary(savedProfessionalSummary);
      }
      if (savedExperience) {
        const exp = JSON.parse(savedExperience);
        setExperience(exp);
        setOriginalExperience(JSON.parse(JSON.stringify(exp)));
      }
      if (savedEducation) {
        const edu = JSON.parse(savedEducation);
        setEducation(edu);
        setOriginalEducation(JSON.parse(JSON.stringify(edu)));
      }
      if (savedSkills) {
        setSkills(savedSkills);
        setOriginalSkills(savedSkills);
      }
    } else {
      // Clear any stale session data for fresh navigation
      sessionStorage.removeItem('jobTailoredResumeId');
      sessionStorage.removeItem('jobTailoredData');
      sessionStorage.removeItem('jobTailoredCurrentStep');
      sessionStorage.removeItem('jobTailoredPersonalInfo');
      sessionStorage.removeItem('jobTailoredProfessionalSummary');
      sessionStorage.removeItem('jobTailoredExperience');
      sessionStorage.removeItem('jobTailoredEducation');
      sessionStorage.removeItem('jobTailoredSkills');
      sessionStorage.removeItem('jobTailoredContinueSession');
    }
  }, [location]);

  // Save resumeId to sessionStorage whenever it changes

  // Save tailoredData to sessionStorage whenever it changes
  useEffect(() => {
    if (tailoredData) {
      sessionStorage.setItem('jobTailoredData', JSON.stringify(tailoredData));
      // Auto-fill personal info from tailored data if not already set
      if (tailoredData.personalInfo && !personalInfo.fullName && !personalInfo.email) {
        setPersonalInfo(tailoredData.personalInfo);
        setOriginalPersonalInfo(tailoredData.personalInfo);
      }
      // Auto-fill other sections if available
      if (tailoredData.professionalSummary && !professionalSummary) {
        setProfessionalSummary(tailoredData.professionalSummary);
        setOriginalProfessionalSummary(tailoredData.professionalSummary);
      }
      if (tailoredData.experience && tailoredData.experience.length > 0 && experience.length === 1 && !experience[0].company) {
        setExperience(tailoredData.experience);
        setOriginalExperience(tailoredData.experience);
      }
      if (tailoredData.education && tailoredData.education.length > 0 && education.length === 1 && !education[0].school) {
        setEducation(tailoredData.education);
        setOriginalEducation(tailoredData.education);
      }
      if (tailoredData.skills && !skills) {
        setSkills(tailoredData.skills);
        setOriginalSkills(tailoredData.skills);
      }
    }
  }, [tailoredData, personalInfo.fullName, personalInfo.email, professionalSummary, experience, education, skills]);

  // Save currentStep to sessionStorage whenever it changes
  useEffect(() => {
    if (currentStep > 0) {
      sessionStorage.setItem('jobTailoredCurrentStep', currentStep.toString());
    }
  }, [currentStep]);

  // Save form data to sessionStorage whenever it changes
  useEffect(() => {
    if (personalInfo.fullName) {
      sessionStorage.setItem('jobTailoredPersonalInfo', JSON.stringify(personalInfo));
    }
  }, [personalInfo]);

  useEffect(() => {
    if (professionalSummary) {
      sessionStorage.setItem('jobTailoredProfessionalSummary', professionalSummary);
    }
  }, [professionalSummary]);

  useEffect(() => {
    if (experience.length > 0 && experience[0].company) {
      sessionStorage.setItem('jobTailoredExperience', JSON.stringify(experience));
    }
  }, [experience]);

  useEffect(() => {
    if (education.length > 0 && education[0].school) {
      sessionStorage.setItem('jobTailoredEducation', JSON.stringify(education));
    }
  }, [education]);

  useEffect(() => {
    if (skills) {
      sessionStorage.setItem('jobTailoredSkills', skills);
    }
  }, [skills]);

  const loadingSteps = useMemo(() => [
    { text: "Analyzing job posting...", duration: 1500 },
    { text: "Extracting resume information...", duration: 2000 },
    { text: "Optimizing for ATS...", duration: 2000 },
    { text: "Finalizing ATS resume...", duration: 1500 }
  ], []);

  // Animate text into textarea (typewriter effect)
  const animateSummary = useCallback((text) => {
    if (!text) {
      setAnimatedProfessionalSummary("");
      return;
    }
    let current = "";
    let i = 0;
    setAnimatedProfessionalSummary("");
    const interval = setInterval(() => {
      if (i < text.length) {
        current += text.charAt(i);
        setAnimatedProfessionalSummary(current);
        i++;
      } else {
        clearInterval(interval);
      }
    }, 18);
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setResumeFile(file);
    }
  };

  const handlePersonalInfoChange = (field, value) => {
    setPersonalInfo({ ...personalInfo, [field]: value });
    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors({ ...validationErrors, [field]: null });
    }
  };

  const addExperience = () => {
    setExperience([...experience, { type: 'work', company: "", position: "", startDate: "", endDate: "", current: false, description: "", location: "", projectName: "", techStack: "", projectLink: "" }]);
  };

  const removeExperience = (index) => {
    const newExperience = experience.filter((_, i) => i !== index);
    setExperience(newExperience);
  };

  const updateExperience = (index, field, value) => {
    const newExperience = [...experience];
    newExperience[index][field] = value;
    setExperience(newExperience);
    // Clear validation error for this field
    const errorKey = `experience_${index}_${field}`;
    if (validationErrors[errorKey]) {
      setValidationErrors({ ...validationErrors, [errorKey]: null });
    }
  };

  const addEducation = () => {
    setEducation([...education, { school: "", degree: "", field: "", graduationYear: "", startYear: "", gpa: "", location: "" }]);
  };

  const removeEducation = (index) => {
    const newEducation = education.filter((_, i) => i !== index);
    setEducation(newEducation);
  };

  const updateEducation = (index, field, value) => {
    const newEducation = [...education];
    newEducation[index][field] = value;
    setEducation(newEducation);
    // Clear validation error for this field
    const errorKey = `education_${index}_${field}`;
    if (validationErrors[errorKey]) {
      setValidationErrors({ ...validationErrors, [errorKey]: null });
    }
  };

  // Language skills functions
  const addLanguageSkill = () => {
    setLanguageSkills([...languageSkills, { language: "", proficiency: "" }]);
  };

  const removeLanguageSkill = (index) => {
    if (languageSkills.length > 1) {
      const newLanguageSkills = languageSkills.filter((_, i) => i !== index);
      setLanguageSkills(newLanguageSkills);
    }
  };

  const updateLanguageSkill = (index, field, value) => {
    const newLanguageSkills = [...languageSkills];
    newLanguageSkills[index][field] = value;
    setLanguageSkills(newLanguageSkills);
  };

  const generatePdfPreview = async () => {
    // Starting PDF generation
    setPdfGenerating(true);
    
    try {
      const token = await refreshTokenIfNeeded();

      if (!token) {
        alert('Please sign in to continue.');
        setPdfGenerating(false);
        return;
      }

      const resumeData = {
        personalInfo,
        professionalSummary,
        experience: experience.filter(exp => exp.company && exp.position),
        education: education.filter(edu => edu.school && edu.degree),
        skills
      };
      
      // Use the same reliable approach as AIResumeBuilder
      let currentResumeId = resumeId;

      if (currentResumeId) {
        // Update existing resume
        await axios.put(
          apiUrl('/api/resume-builder/update-step'),
          {
            resumeId: currentResumeId,
            step: 4,
            stepData: resumeData
          },
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            withCredentials: true
          }
        );
      } else {
        // Create new resume from scratch
        const createResponse = await axios.post(
          apiUrl('/api/resume-builder/create-from-scratch'),
          {
            personalInfo: resumeData.personalInfo,
            professionalSummary: resumeData.professionalSummary,
            experience: resumeData.experience,
            education: resumeData.education,
            skills: resumeData.skills,
            jobDescription: 'Job-tailored resume created from existing tailored data'
          },
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            withCredentials: true
          }
        );

        currentResumeId = createResponse.data.resumeId;

        // Update the resumeId in state and sessionStorage
        setResumeId(currentResumeId);
        sessionStorage.setItem('jobTailoredResumeId', currentResumeId);
      }

      // Generate PDF
      const pdfResponse = await axios.post(
        apiUrl('/api/resume-builder/generate-pdf'),
        { resumeId: currentResumeId },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          withCredentials: true
        }
      );

      if (pdfResponse.data.success && pdfResponse.data.pdfUrl) {
        // PDF generated successfully
      } else {
        console.error('PDF generation failed - no URL returned');
        // Removed alert - PDF generation handled gracefully
      }
      
    } catch (error) {
      console.error("PDF generation failed:", error);
      // Removed alert - error handled gracefully
    } finally {
      setPdfGenerating(false);
    }
  };

  const handleGenerateResume = async (e) => {
    e.preventDefault();
    
    // Check if user can generate questions
    if (!canCreateTailoredResume()) {
      const usageInfo = getResumeUsageInfo();
      alert(`You've reached your usage limit (${usageInfo?.used}/${usageInfo?.limit}). Please upgrade to continue.`);
      return;
    }

    // Move to step 4 for preview
    await nextStep();
  };

  const nextStep = async () => {
    if (currentStep === 3) {
      // Generate PDF when moving to step 4
      await generatePdfPreview();
    }
    setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();

    if (!resumeFile) {
      alert("Please upload your resume file.");
      return;
    }

    setIsLoading(true);
    setCurrentStep(0);

    try {
      const token = await refreshTokenIfNeeded();

      if (!token) {
        alert("Please sign in to continue.");
        setIsLoading(false);
        return;
      }

      // Start progress animation
      for (let i = 0; i < loadingSteps.length; i++) {
        setCurrentStep(i);
        await new Promise(resolve => setTimeout(resolve, loadingSteps[i].duration));
      }

      // Call backend API to analyze job and resume
      const formData = new FormData();
      formData.append('job_url', jobUrl);
      formData.append('cv_file', resumeFile);
      formData.append('language', language);
      
      const response = await axios.post(
        apiUrl('/api/resume-builder/upload-and-tailor'),
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          withCredentials: true
        }
      );
      

      // Set the tailored data and auto-fill the form
      setTailoredData(response.data.tailoredResume);
      setResumeId(response.data.resumeId);

      // Auto-fill form data
      const data = response.data.tailoredResume;
      // Received tailored data
      
      // Personal Info - ensure all fields are properly set
      if (data.personalInfo) {
        const personalInfoData = {
          fullName: data.personalInfo.fullName || data.personalInfo.name || '',
          email: data.personalInfo.email || '',
          phone: data.personalInfo.phone || data.personalInfo.phoneNumber || '',
          location: data.personalInfo.location || data.personalInfo.address || '',
          linkedIn: data.personalInfo.linkedIn || data.personalInfo.linkedin || '',
          portfolio: data.personalInfo.portfolio || data.personalInfo.website || ''
        };
        
        setPersonalInfo(personalInfoData);
        setOriginalPersonalInfo(personalInfoData);
        
        // Save to sessionStorage immediately
        sessionStorage.setItem('jobTailoredPersonalInfo', JSON.stringify(personalInfoData));
      }
      // Professional Summary
      if (data.professionalSummary) {
        // Limit summary to 3 lines
        const summaryLines = data.professionalSummary.split(/\r?\n/).filter(line => line.trim() !== '');
        let limitedSummary = summaryLines.slice(0, 3).join('\n');
        if (summaryLines.length > 3) {
          // Removed alert for summary exceeding 3 lines
        }
        setProfessionalSummary(limitedSummary);
        setOriginalProfessionalSummary(limitedSummary);
        animateSummary(limitedSummary);
        
        // Save to sessionStorage
        sessionStorage.setItem('jobTailoredProfessionalSummary', limitedSummary);
      }
      
      // Experience
      if (data.experience && data.experience.length > 0) {
        // Ensure description is an array of bullet points
        const exp = data.experience.map(expItem => {
          let desc = expItem.description;
          if (typeof desc === 'string') {
            desc = desc.split(/[.!?]+/).map(d => d.trim()).filter(d => d);
          }
          if (!Array.isArray(desc)) desc = [desc];
          
          // Convert date formats from MM/YYYY to YYYY-MM for HTML month inputs
          const convertDateFormat = (dateStr) => {
            if (!dateStr || typeof dateStr !== 'string') return '';
            const parts = dateStr.split('/');
            if (parts.length === 2) {
              const [month, year] = parts;
              return `${year}-${month.padStart(2, '0')}`;
            }
            return dateStr;
          };
          
          return { 
            ...expItem, 
            description: desc,
            startDate: convertDateFormat(expItem.startDate),
            endDate: convertDateFormat(expItem.endDate)
          };
        });
        setExperience(exp);
        setOriginalExperience(JSON.parse(JSON.stringify(exp)));
        
        // Save to sessionStorage
        sessionStorage.setItem('jobTailoredExperience', JSON.stringify(exp));
      }
      
      // Education
      if (data.education && data.education.length > 0) {
        const edu = data.education.map(eduItem => {
          // Convert date formats from MM/YYYY to YYYY-MM for HTML month inputs
          const convertDateFormat = (dateStr) => {
            if (!dateStr || typeof dateStr !== 'string') return '';
            const parts = dateStr.split('/');
            if (parts.length === 2) {
              const [month, year] = parts;
              return `${year}-${month.padStart(2, '0')}`;
            }
            // If it's just a year (YYYY), convert to YYYY-01
            if (dateStr.match(/^\d{4}$/)) {
              return `${dateStr}-01`;
            }
            return dateStr;
          };
          
          return {
            ...eduItem,
            graduationYear: convertDateFormat(eduItem.graduationYear),
            startYear: convertDateFormat(eduItem.startYear)
          };
        });
        setEducation(edu);
        setOriginalEducation(JSON.parse(JSON.stringify(edu)));
        
        // Save to sessionStorage
        sessionStorage.setItem('jobTailoredEducation', JSON.stringify(edu));
      }
      
      // Skills
      if (data.skills) {
        setSkills(data.skills);
        setOriginalSkills(data.skills);
        
        // Save to sessionStorage
        sessionStorage.setItem('jobTailoredSkills', data.skills);
      }

      // Language Skills - Filter out duplicates and invalid entries
      if (data.languageSkills && data.languageSkills.length > 0) {
        // Clean up language skills: remove duplicates and invalid entries
        const validLanguages = data.languageSkills.filter(lang => 
          lang.language && 
          lang.proficiency && 
          lang.language.trim() !== '' && 
          lang.proficiency.trim() !== ''
        );
        
        // Remove duplicates - keep only one entry per language (first occurrence)
        const uniqueLanguages = [];
        const seenLanguages = new Set();
        
        validLanguages.forEach(lang => {
          const langName = lang.language.toLowerCase();
          if (!seenLanguages.has(langName)) {
            seenLanguages.add(langName);
            uniqueLanguages.push(lang);
          }
        });
        
        // If we have valid languages, use them; otherwise default to English
        if (uniqueLanguages.length > 0) {
          setLanguageSkills(uniqueLanguages);
          sessionStorage.setItem('jobTailoredLanguageSkills', JSON.stringify(uniqueLanguages));
        } else {
          // Default to English if no valid languages found
          const defaultLang = [{ language: "English", proficiency: "Native" }];
          setLanguageSkills(defaultLang);
          sessionStorage.setItem('jobTailoredLanguageSkills', JSON.stringify(defaultLang));
        }
      } else {
        // No language skills provided - default to English
        const defaultLang = [{ language: "English", proficiency: "Native" }];
        setLanguageSkills(defaultLang);
        sessionStorage.setItem('jobTailoredLanguageSkills', JSON.stringify(defaultLang));
      }

      setIsLoading(false);
      setCurrentStep(1);

      // Save data to sessionStorage for persistence and set continue session flag
      sessionStorage.setItem('jobTailoredResumeId', response.data.resumeId);
      sessionStorage.setItem('jobTailoredData', JSON.stringify(response.data.tailoredResume));
      sessionStorage.setItem('jobTailoredCurrentStep', '1');
      sessionStorage.setItem('jobTailoredContinueSession', 'true');

    } catch (error) {
      console.error("Failed to process resume:", error);
      // Removed alert - error handled gracefully
      setIsLoading(false);
      setCurrentStep(0);
    }
  }, [resumeFile, jobUrl, language, refreshTokenIfNeeded, loadingSteps, animateSummary]);

  // Restore session data on component mount or location change
  useEffect(() => {
    const savedResumeId = sessionStorage.getItem('jobTailoredResumeId');
    const savedData = sessionStorage.getItem('jobTailoredData');
    const savedCurrentStep = sessionStorage.getItem('jobTailoredCurrentStep');
    const savedPersonalInfo = sessionStorage.getItem('jobTailoredPersonalInfo');
    const savedProfessionalSummary = sessionStorage.getItem('jobTailoredProfessionalSummary');
    const savedExperience = sessionStorage.getItem('jobTailoredExperience');
    const savedEducation = sessionStorage.getItem('jobTailoredEducation');
    const savedSkills = sessionStorage.getItem('jobTailoredSkills');
    const savedLanguageSkills = sessionStorage.getItem('jobTailoredLanguageSkills');
    const continueSession = sessionStorage.getItem('jobTailoredContinueSession');

    if (continueSession === 'true' && savedData) {
      // Restore from sessionStorage
      setTailoredData(JSON.parse(savedData));
      if (savedResumeId) {
        setResumeId(savedResumeId);
      }
      if (savedCurrentStep) {
        setCurrentStep(parseInt(savedCurrentStep));
      }
      if (savedPersonalInfo) {
        setPersonalInfo(JSON.parse(savedPersonalInfo));
        setOriginalPersonalInfo(JSON.parse(savedPersonalInfo));
      }
      if (savedProfessionalSummary) {
        setProfessionalSummary(savedProfessionalSummary);
        setOriginalProfessionalSummary(savedProfessionalSummary);
      }
      if (savedExperience) {
        const exp = JSON.parse(savedExperience);
        // Convert date formats from MM/YYYY to YYYY-MM for HTML month inputs when restoring
        const convertDateFormat = (dateStr) => {
          if (!dateStr || typeof dateStr !== 'string') return '';
          const parts = dateStr.split('/');
          if (parts.length === 2) {
            const [month, year] = parts;
            return `${year}-${month.padStart(2, '0')}`;
          }
          // If it's just a year (YYYY), convert to YYYY-01
          if (dateStr.match(/^\d{4}$/)) {
            return `${dateStr}-01`;
          }
          return dateStr;
        };
        
        const convertedExp = exp.map(expItem => ({
          ...expItem,
          startDate: convertDateFormat(expItem.startDate),
          endDate: convertDateFormat(expItem.endDate)
        }));
        setExperience(convertedExp);
        setOriginalExperience(JSON.parse(JSON.stringify(convertedExp)));
      }
      if (savedEducation) {
        const edu = JSON.parse(savedEducation);
        // Convert date formats from MM/YYYY to YYYY-MM for HTML month inputs when restoring
        const convertDateFormat = (dateStr) => {
          if (!dateStr || typeof dateStr !== 'string') return '';
          const parts = dateStr.split('/');
          if (parts.length === 2) {
            const [month, year] = parts;
            return `${year}-${month.padStart(2, '0')}`;
          }
          // If it's just a year (YYYY), convert to YYYY-01
          if (dateStr.match(/^\d{4}$/)) {
            return `${dateStr}-01`;
          }
          return dateStr;
        };
        
        const convertedEdu = edu.map(eduItem => ({
          ...eduItem,
          graduationYear: convertDateFormat(eduItem.graduationYear),
          startYear: convertDateFormat(eduItem.startYear)
        }));
        setEducation(convertedEdu);
        setOriginalEducation(JSON.parse(JSON.stringify(convertedEdu)));
      }
      if (savedSkills) {
        setSkills(savedSkills);
        setOriginalSkills(savedSkills);
      }
      if (savedLanguageSkills) {
        setLanguageSkills(JSON.parse(savedLanguageSkills));
      }
    } else {
      // Clear any stale session data for fresh navigation
      sessionStorage.removeItem('jobTailoredResumeId');
      sessionStorage.removeItem('jobTailoredData');
      sessionStorage.removeItem('jobTailoredCurrentStep');
      sessionStorage.removeItem('jobTailoredPersonalInfo');
      sessionStorage.removeItem('jobTailoredProfessionalSummary');
      sessionStorage.removeItem('jobTailoredExperience');
      sessionStorage.removeItem('jobTailoredEducation');
      sessionStorage.removeItem('jobTailoredSkills');
      sessionStorage.removeItem('jobTailoredLanguageSkills');
      sessionStorage.removeItem('jobTailoredContinueSession');
    }
  }, [location, handleSubmit]);

  // AI Assistance functions
  const getAiAssistance = async (fieldType, currentData, context = '') => {
    setAiLoading(true);
    setAiFieldType(fieldType);

    try {
      const token = await refreshTokenIfNeeded();

      if (!token) {
        alert('Please sign in to use AI assistance.');
        return;
      }

      const response = await axios.post(
        apiUrl('/api/resume-builder/ai-assist'),
        {
          fieldType,
          currentData,
          context,
          language,
          resumeId
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          withCredentials: true
        }
      );

      if (response.data.success) {
        return response.data.suggestion;
      }
    } catch (error) {
      console.error('AI assistance error:', error);
      // Removed alert - error handled gracefully
    } finally {
      setAiLoading(false);
      setAiFieldType(null);
    }
  };

  const handleAiProfessionalSummary = async () => {
    const suggestion = await getAiAssistance('professional-summary', {
      personalInfo,
      experience: experience.filter(exp => exp.company || exp.position),
      education: education.filter(edu => edu.school || edu.degree),
      skills
    }, 'Write a concise 3-line ATS-compliant professional summary for a resume. Do not include the user\'s name or location. Focus on skills, adaptability, and value to employers.');

    if (suggestion) {
      setProfessionalSummary(suggestion);
      animateSummary(suggestion);
    }
  };

  // ...existing code...
  // Remove frontend prompt logic for quantifiable results in experience bullets.
  const handleAiExperienceDescription = async (index) => {
    const exp = experience[index];
    const suggestion = await getAiAssistance(
      'experience-description',
      exp
      // No custom prompt here; backend handles quantifiable results logic.
    );

    if (suggestion) {
      updateExperience(index, 'description', suggestion);
    }
  };

  const handleAiSkills = async () => {
    // Determine engineering field from education
    const engineeringField = education.find(edu => edu.field)?.field || 'engineering';
    const fieldContext = engineeringField.toLowerCase().includes('mechanical') ? 'mechanical engineering' :
                        engineeringField.toLowerCase().includes('civil') ? 'civil engineering' :
                        engineeringField.toLowerCase().includes('electrical') ? 'electrical engineering' :
                        engineeringField.toLowerCase().includes('chemical') ? 'chemical engineering' :
                        engineeringField.toLowerCase().includes('aerospace') ? 'aerospace engineering' :
                        engineeringField.toLowerCase().includes('biomedical') ? 'biomedical engineering' :
                        engineeringField.toLowerCase().includes('computer') ? 'computer engineering' :
                        'engineering';

    // Include language skills in the context
    const languageSkillsText = languageSkills
      .filter(lang => lang.language && lang.proficiency)
      .map(lang => `${lang.proficiency} in ${lang.language}`)
      .join(', ');

    const context = `Generate a comprehensive list of technical and soft skills for a ${fieldContext} professional. Include both technical skills specific to ${fieldContext} (like software tools, technical competencies, and industry-specific knowledge) and essential soft skills. ${languageSkillsText ? `Also include these language skills: ${languageSkillsText}.` : ''} Format as comma-separated values. Focus on skills that would be valuable in ${fieldContext} job applications.`;

    const suggestion = await getAiAssistance('skills', {
      skills,
      experience: experience.filter(exp => exp.company || exp.position),
      education: education.filter(edu => edu.school || edu.degree),
      languageSkills: languageSkills.filter(lang => lang.language && lang.proficiency)
    }, context);

    if (suggestion) {
      // Combine existing skills with AI suggestion, but preserve language skills
      const existingSkills = skills ? skills.split(',').map(s => s.trim()).filter(s => s) : [];
      const suggestedSkills = suggestion.split(',').map(s => s.trim()).filter(s => s);
      
      // Add language skills to the suggested skills
      const languageSkillStrings = languageSkills
        .filter(lang => lang.language && lang.proficiency)
        .map(lang => `${lang.proficiency} in ${lang.language}`);
      
      const combinedSkills = [...new Set([...existingSkills, ...suggestedSkills, ...languageSkillStrings])];
      setSkills(combinedSkills.join(', '));
    }
  };



  // Handle Keep and Undo for steps
  const handleKeep = (step) => {
    // Proceed to next step
    nextStep();
  };

  const handleUndo = (step) => {
    if (step === 1) {
      setPersonalInfo(originalPersonalInfo);
      setProfessionalSummary(originalProfessionalSummary);
      setAnimatedProfessionalSummary(originalProfessionalSummary);
    } else if (step === 2) {
      setExperience(JSON.parse(JSON.stringify(originalExperience)));
    } else if (step === 3) {
      setEducation(JSON.parse(JSON.stringify(originalEducation)));
      setSkills(originalSkills);
    }
  };

  // Session conflict modal handlers
  const handleContinueSession = () => {
    // Load the existing session data
    const savedResumeId = sessionStorage.getItem('jobTailoredResumeId');
    const savedTailoredData = sessionStorage.getItem('jobTailoredData');
    const savedCurrentStep = sessionStorage.getItem('jobTailoredCurrentStep');
    const savedPersonalInfo = sessionStorage.getItem('jobTailoredPersonalInfo');
    const savedProfessionalSummary = sessionStorage.getItem('jobTailoredProfessionalSummary');
    const savedExperience = sessionStorage.getItem('jobTailoredExperience');
    const savedEducation = sessionStorage.getItem('jobTailoredEducation');
    const savedSkills = sessionStorage.getItem('jobTailoredSkills');

    if (savedResumeId) {
      setResumeId(savedResumeId);
    }
    if (savedTailoredData) {
      setTailoredData(JSON.parse(savedTailoredData));
    }
    if (savedCurrentStep) {
      setCurrentStep(parseInt(savedCurrentStep));
    }
    if (savedPersonalInfo) {
      setPersonalInfo(JSON.parse(savedPersonalInfo));
      setOriginalPersonalInfo(JSON.parse(savedPersonalInfo));
    }
    if (savedProfessionalSummary) {
      setProfessionalSummary(savedProfessionalSummary);
      setOriginalProfessionalSummary(savedProfessionalSummary);
    }
    if (savedExperience) {
      const exp = JSON.parse(savedExperience);
      setExperience(exp);
      setOriginalExperience(JSON.parse(JSON.stringify(exp)));
    }
    if (savedEducation) {
      const edu = JSON.parse(savedEducation);
      setEducation(edu);
      setOriginalEducation(JSON.parse(JSON.stringify(edu)));
    }
    if (savedSkills) {
      setSkills(savedSkills);
      setOriginalSkills(savedSkills);
    }

    setShowSessionConflictModal(false);
    setHasIncompleteSession(false);
  };

  const handleStartNewSession = () => {
    // Clear all session data and start fresh
    sessionStorage.removeItem('jobTailoredResumeId');
    sessionStorage.removeItem('jobTailoredData');
    sessionStorage.removeItem('jobTailoredCurrentStep');
    sessionStorage.removeItem('jobTailoredPersonalInfo');
    sessionStorage.removeItem('jobTailoredProfessionalSummary');
    sessionStorage.removeItem('jobTailoredExperience');
    sessionStorage.removeItem('jobTailoredEducation');
    sessionStorage.removeItem('jobTailoredSkills');
    sessionStorage.removeItem('jobTailoredContinueSession');

    // Reset all state
    setResumeId(null);
    setTailoredData(null);
    setCurrentStep(0);
    setPersonalInfo({
      fullName: "",
      email: "",
      phone: "",
      location: "",
      linkedIn: "",
      portfolio: ""
    });
    setProfessionalSummary("");
    setExperience([{
      type: 'work',
      company: "",
      position: "",
      startDate: "",
      endDate: "",
      current: false,
      description: "",
      projectName: "",
      techStack: "",
      projectLink: ""
    }]);
    setEducation([{
      school: "",
      degree: "",
      field: "",
      graduationYear: "",
      startYear: "",
      gpa: "",
      location: ""
    }]);
    setSkills("");
    setOriginalPersonalInfo({
      fullName: "",
      email: "",
      phone: "",
      location: "",
      linkedIn: "",
      portfolio: ""
    });
    setOriginalProfessionalSummary("");
    setOriginalExperience([{
      type: 'work',
      company: "",
      position: "",
      startDate: "",
      endDate: "",
      current: false,
      description: "",
      projectName: "",
      techStack: "",
      projectLink: ""
    }]);
    setOriginalEducation([{
      school: "",
      degree: "",
      field: "",
      graduationYear: "",
      startYear: "",
      gpa: "",
      location: ""
    }]);
    setOriginalSkills("");

    setShowSessionConflictModal(false);
    setHasIncompleteSession(false);
  };

  return (
    <div className="dashboard-container">
      <Sidebar activeItem="ai-resume-builder" />
      <div className="main-content">
        <div className="content-header">
          <h2>Job Tailored Resume</h2>
          <p>Upload your resume and job posting. LeetEngineer will create an ATS-optimized CV<br />tailored to your target role for maximum impact.</p>
        </div>

        {tailoredData && (
          <div className="resume-builder-progress">
            <div className={`progress-step ${currentStep > 1 ? 'completed' : currentStep === 1 ? 'active' : ''}`}>
              <div className="step-circle">
                {currentStep > 1 ? '✓' : '1'}
              </div>
              <span>Personal Info</span>
            </div>
            <div className={`progress-step ${currentStep > 2 ? 'completed' : currentStep === 2 ? 'active' : ''}`}>
              <div className="step-circle">
                {currentStep > 2 ? '✓' : '2'}
              </div>
              <span>Experience</span>
            </div>
            <div className={`progress-step ${currentStep > 3 ? 'completed' : currentStep === 3 ? 'active' : ''}`}>
              <div className="step-circle">
                {currentStep > 3 ? '✓' : '3'}
              </div>
              <span>Education & Skills</span>
            </div>
            <div className={`progress-step ${currentStep === 4 ? 'active' : ''}`}>
              <div className="step-circle">4</div>
              <span>Review & Download</span>
            </div>
          </div>
        )}

        {isLoading ? (
          <div className="form-container resume-form-container" style={{ maxWidth: '600px', margin: '2rem auto' }}>
            <div className="loading-progress">
              <div className="loading-spinner resume-spinner"></div>
              <div className="loading-steps">
                {loadingSteps.map((step, index) => (
                  <div
                    key={index}
                    className={`loading-step ${index <= currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''}`}
                  >
                    <div className="step-indicator">
                      {index < currentStep ? (
                        <span className="checkmarks">✓</span>
                      ) : (
                        <span className="step-number">{index + 1}</span>
                      )}
                    </div>
                    <span className="step-text">{step.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : tailoredData ? (
          <div className="resume-builder-container">
            <div className="form-container resume-form-container">
              <form onSubmit={handleGenerateResume}>
                {/* Step 1: Personal Information */}
                {currentStep === 1 && (
                    <div className="resume-step">
                      <h3>Personal Information</h3>
                      <div className="form-group">
                        <label>Full Name</label>
                        <input
                          type="text"
                          value={personalInfo.fullName || ''}
                          onChange={(e) => handlePersonalInfoChange('fullName', e.target.value)}
                          placeholder="John Doe"
                          className={validationErrors.fullName ? 'error' : ''}
                          required
                        />
                        {validationErrors.fullName && <span className="error-message">{validationErrors.fullName}</span>}
                      </div>
                      <div className="form-row">
                        <div className="form-group">
                          <label>Email</label>
                          <input
                            type="email"
                            value={personalInfo.email || ''}
                            onChange={(e) => handlePersonalInfoChange('email', e.target.value)}
                            placeholder="john.doe@example.com"
                            className={validationErrors.email ? 'error' : ''}
                            required
                          />
                          {validationErrors.email && <span className="error-message">{validationErrors.email}</span>}
                        </div>
                        <div className="form-group">
                          <label>Phone</label>
                          <input
                            type="tel"
                            value={personalInfo.phone || ''}
                            onChange={(e) => handlePersonalInfoChange('phone', e.target.value)}
                            placeholder="+1 (555) 123-4567"
                            className={validationErrors.phone ? 'error' : ''}
                            required
                          />
                          {validationErrors.phone && <span className="error-message">{validationErrors.phone}</span>}
                        </div>
                      </div>
                      <div className="form-group">
                        <label>Location</label>
                        <input
                          type="text"
                          value={personalInfo.location || ''}
                          onChange={(e) => handlePersonalInfoChange('location', e.target.value)}
                          placeholder="San Francisco, CA"
                          className={validationErrors.location ? 'error' : ''}
                          required
                        />
                        {validationErrors.location && <span className="error-message">{validationErrors.location}</span>}
                      </div>
                      <div className="form-row">
                        <div className="form-group">
                          <label>LinkedIn Profile</label>
                          <input
                            type="url"
                            value={personalInfo.linkedIn}
                            onChange={(e) => handlePersonalInfoChange('linkedIn', e.target.value)}
                            placeholder="https://linkedin.com/in/johndoe"
                          />
                        </div>
                        <div className="form-group">
                          <label>Portfolio/Website</label>
                          <input
                            type="url"
                            value={personalInfo.portfolio}
                            onChange={(e) => handlePersonalInfoChange('portfolio', e.target.value)}
                            placeholder="https://johndoe.com"
                          />
                        </div>
                      </div>
                      <div className="form-group">
                        <label>Professional Summary</label>
                        <div className="field-with-ai">
                          <textarea
                            value={animatedProfessionalSummary || professionalSummary}
                            onChange={(e) => {
                              setProfessionalSummary(e.target.value);
                              setAnimatedProfessionalSummary("");
                              // Clear validation error for professional summary
                              if (validationErrors.professionalSummary) {
                                setValidationErrors({ ...validationErrors, professionalSummary: null });
                              }
                            }}
                            placeholder="Brief overview of your professional background and career objectives..."
                            rows="4"
                            className={validationErrors.professionalSummary ? 'error' : ''}
                          />
                          {validationErrors.professionalSummary && <span className="error-message">{validationErrors.professionalSummary}</span>}
                          <button 
                            type="button" 
                            className="ai-assist-btn"
                            onClick={handleAiProfessionalSummary}
                            disabled={aiLoading}
                          >
                            {aiLoading && aiFieldType === 'professional-summary' ? (
                              <>
                                <span className="ai-spinner">⟳</span> Generating...
                              </>
                            ) : (
                              <>
                                <span className="ai-icon">✨</span> AI Write
                              </>
                            )}
                          </button>
                        </div>
                      </div>

                      {/* Keep/Undo buttons for Step 1 - Only show if pre-filled from Job Tailored */}
                      {tailoredData && (
                        <div className="action-buttons">
                          <span className="action-message">
                            ✨ LeetEngineer optimized your contact info for ATS compatibility tailored to the job posting. Ready to proceed?
                          </span>
                          <div className="action-buttons-group">
                            <button type="button" className="keep-btn" onClick={() => handleKeep(1)}>
                              <span className="btn-icon">✓</span> Keep
                            </button>
                            <button type="button" className="undo-btn" onClick={() => handleUndo(1)}>
                              <span className="btn-icon">↶</span> Undo
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Step 2: Experience & Projects */}
                  {currentStep === 2 && (
                    <div className="resume-step">
                      <h3>Experience & Projects</h3>
                      {experience.map((exp, index) => (
                        <div key={index} className="experience-entry">
                          <div className="entry-header">
                            <h4>Type</h4>
                            {experience.length > 1 && (
                              <button
                                type="button"
                                className="remove-btn"
                                onClick={() => removeExperience(index)}
                              >
                                Remove
                              </button>
                            )}
                          </div>

                          {/* Type Selector */}
                          <div className="form-group type-selector-group">
                            <div className="type-selector">
                              <label className="radio-label">
                                <input
                                  type="radio"
                                  name={`type-${index}`}
                                  value="work"
                                  checked={exp.type === 'work'}
                                  onChange={(e) => updateExperience(index, 'type', e.target.value)}
                                />
                                <span>Work Experience</span>
                              </label>
                              <label className="radio-label">
                                <input
                                  type="radio"
                                  name={`type-${index}`}
                                  value="project"
                                  checked={exp.type === 'project'}
                                  onChange={(e) => updateExperience(index, 'type', e.target.value)}
                                />
                                <span>Personal Project</span>
                              </label>
                            </div>
                          </div>

                          {exp.type === 'work' ? (
                            <>
                              <div className="form-row">
                                <div className="form-group">
                                  <label>Company</label>
                                  <input
                                    type="text"
                                    value={exp.company}
                                    onChange={(e) => updateExperience(index, 'company', e.target.value)}
                                    placeholder="Company Name"
                                    className={validationErrors[`experience_${index}_company`] ? 'error' : ''}
                                    required
                                  />
                                  {validationErrors[`experience_${index}_company`] && <span className="error-message">{validationErrors[`experience_${index}_company`]}</span>}
                                </div>
                                <div className="form-group">
                                  <label>Position</label>
                                  <input
                                    type="text"
                                    value={exp.position}
                                    onChange={(e) => updateExperience(index, 'position', e.target.value)}
                                    placeholder="Job Title"
                                    className={validationErrors[`experience_${index}_position`] ? 'error' : ''}
                                    required
                                  />
                                  {validationErrors[`experience_${index}_position`] && <span className="error-message">{validationErrors[`experience_${index}_position`]}</span>}
                                </div>
                              </div>
                              <div className="form-row">
                                <div className="form-group">
                                  <label>Start Date</label>
                                  <input
                                    type="month"
                                    value={exp.startDate}
                                    onChange={(e) => updateExperience(index, 'startDate', e.target.value)}
                                    className={validationErrors[`experience_${index}_startDate`] ? 'error' : ''}
                                    required
                                  />
                                  {validationErrors[`experience_${index}_startDate`] && <span className="error-message">{validationErrors[`experience_${index}_startDate`]}</span>}
                                </div>
                                <div className="form-group">
                                  <label>End Date</label>
                                  <input
                                    type="month"
                                    value={exp.endDate}
                                    onChange={(e) => updateExperience(index, 'endDate', e.target.value)}
                                    disabled={exp.current}
                                    className={validationErrors[`experience_${index}_endDate`] ? 'error' : ''}
                                    required={!exp.current}
                                  />
                                  {validationErrors[`experience_${index}_endDate`] && <span className="error-message">{validationErrors[`experience_${index}_endDate`]}</span>}
                                </div>
                              </div>
                              <div className="form-group">
                                <label className="checkbox-label">
                                  <input
                                    type="checkbox"
                                    checked={exp.current}
                                    onChange={(e) => {
                                      updateExperience(index, 'current', e.target.checked);
                                      if (e.target.checked) {
                                        updateExperience(index, 'endDate', '');
                                      }
                                    }}
                                  />
                                  <span>I currently work here</span>
                                </label>
                              </div>
                              <div className="form-group">
                                <label>Location (City, State/Country)</label>
                                <input
                                  type="text"
                                  value={exp.location}
                                  onChange={(e) => updateExperience(index, 'location', e.target.value)}
                                  placeholder="e.g., San Francisco, CA"
                                />
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="form-row">
                                <div className="form-group">
                                  <label>Project Name</label>
                                  <input
                                    type="text"
                                    value={exp.projectName}
                                    onChange={(e) => updateExperience(index, 'projectName', e.target.value)}
                                    placeholder="Project Name"
                                    className={validationErrors[`experience_${index}_projectName`] ? 'error' : ''}
                                    required
                                  />
                                  {validationErrors[`experience_${index}_projectName`] && <span className="error-message">{validationErrors[`experience_${index}_projectName`]}</span>}
                                </div>
                                <div className="form-group">
                                  <label>Tools & Technologies</label>
                                  <input
                                    type="text"
                                    value={exp.techStack}
                                    onChange={(e) => updateExperience(index, 'techStack', e.target.value)}
                                    placeholder="AutoCAD, SolidWorks, MATLAB, PLC Programming..."
                                  />
                                </div>
                              </div>
                              <div className="form-row">
                                <div className="form-group">
                                  <label>Start Date</label>
                                  <input
                                    type="month"
                                    value={exp.startDate}
                                    onChange={(e) => updateExperience(index, 'startDate', e.target.value)}
                                    className={validationErrors[`experience_${index}_startDate`] ? 'error' : ''}
                                    required
                                  />
                                  {validationErrors[`experience_${index}_startDate`] && <span className="error-message">{validationErrors[`experience_${index}_startDate`]}</span>}
                                </div>
                                <div className="form-group">
                                  <label>End Date</label>
                                  <input
                                    type="month"
                                    value={exp.endDate}
                                    onChange={(e) => updateExperience(index, 'endDate', e.target.value)}
                                    disabled={exp.current}
                                    className={validationErrors[`experience_${index}_endDate`] ? 'error' : ''}
                                    required={!exp.current}
                                  />
                                  {validationErrors[`experience_${index}_endDate`] && <span className="error-message">{validationErrors[`experience_${index}_endDate`]}</span>}
                                </div>
                              </div>
                              <div className="form-group">
                                <label className="checkbox-label">
                                  <input
                                    type="checkbox"
                                    checked={exp.current}
                                    onChange={(e) => {
                                      updateExperience(index, 'current', e.target.checked);
                                      if (e.target.checked) {
                                        updateExperience(index, 'endDate', '');
                                      }
                                    }}
                                  />
                                  <span>This is an ongoing project</span>
                                </label>
                              </div>
                              <div className="form-group">
                                <label>Project Link</label>
                                <input
                                  type="url"
                                  value={exp.projectLink}
                                  onChange={(e) => updateExperience(index, 'projectLink', e.target.value)}
                                  placeholder="https://github.com/username/project"
                                />
                              </div>
                            </>
                          )}

                          <div className="form-group">
                            <label>Description</label>
                            <div className="field-with-ai">
                              <textarea
                                value={Array.isArray(exp.description) ? exp.description.map(d => {
                                  // Remove existing bullet symbols and add clean bullet
                                  const cleanDesc = d.replace(/^[-•]\s*/, '');
                                  return `• ${cleanDesc}`;
                                }).join('\n') : exp.description}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  const lines = value.split('\n').map(line => line.replace(/^[-•]\s*/, '').trim()).filter(line => line);
                                  updateExperience(index, 'description', lines);
                                }}
                                placeholder={exp.type === 'work' ? "Describe your responsibilities, achievements, and key projects..." : "Describe the project, your role, tools/technologies used, and outcomes..."}
                                rows="4"
                                className={validationErrors[`experience_${index}_description`] ? 'error' : ''}
                                required
                              />
                              {validationErrors[`experience_${index}_description`] && <span className="error-message">{validationErrors[`experience_${index}_description`]}</span>}
                              <button 
                                type="button" 
                                className="ai-assist-btn"
                                onClick={() => handleAiExperienceDescription(index)}
                                disabled={aiLoading || (exp.type === 'work' ? (!exp.position || !exp.company) : !exp.projectName)}
                                title={exp.type === 'work' ? (!exp.position || !exp.company ? "Fill in position and company first" : "Get AI suggestions") : (!exp.projectName ? "Fill in project name first" : "Get AI suggestions")}
                              >
                                {aiLoading && aiFieldType === 'experience-description' ? (
                                  <>
                                    <span className="ai-spinner">⟳</span> Generating...
                                  </>
                                ) : (
                                  <>
                                    <span className="ai-icon">✨</span> AI Write
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                      <button type="button" className="add-btn" onClick={addExperience}>
                        + Add Experience
                      </button>

                      {/* Keep/Undo buttons for Step 2 - Only show if pre-filled from Job Tailored */}
                      {tailoredData && (
                        <div className="action-buttons">
                          <span className="action-message">
                            🚀 LeetEngineer restructured your experience to highlight job-relevant achievements. Perfect for ATS?
                          </span>
                          <div className="action-buttons-group">
                            <button type="button" className="keep-btn" onClick={() => handleKeep(2)}>
                              <span className="btn-icon">✓</span> Keep
                            </button>
                            <button type="button" className="undo-btn" onClick={() => handleUndo(2)}>
                              <span className="btn-icon">↶</span> Undo
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Step 3: Education & Skills */}
                  {currentStep === 3 && (
                    <div className="resume-step">
                      <h3>Education</h3>
                      {education.map((edu, index) => (
                        <div key={index} className="education-entry">
                          <div className="entry-header">
                            <h4>Education {index + 1}</h4>
                            {education.length > 1 && (
                              <button
                                type="button"
                                className="remove-btn"
                                onClick={() => removeEducation(index)}
                              >
                                Remove
                              </button>
                            )}
                          </div>
                          <div className="form-row">
                            <div className="form-group">
                              <label>School/University</label>
                              <input
                                type="text"
                                value={edu.school}
                                onChange={(e) => updateEducation(index, 'school', e.target.value)}
                                placeholder="University Name"
                                className={validationErrors[`education_${index}_school`] ? 'error' : ''}
                                required
                              />
                              {validationErrors[`education_${index}_school`] && <span className="error-message">{validationErrors[`education_${index}_school`]}</span>}
                            </div>
                            <div className="form-group">
                              <label>Degree</label>
                              <input
                                type="text"
                                value={edu.degree}
                                onChange={(e) => updateEducation(index, 'degree', e.target.value)}
                                placeholder="Bachelor of Science"
                                className={validationErrors[`education_${index}_degree`] ? 'error' : ''}
                                required
                              />
                              {validationErrors[`education_${index}_degree`] && <span className="error-message">{validationErrors[`education_${index}_degree`]}</span>}
                            </div>
                          </div>
                          <div className="form-row">
                            <div className="form-group">
                              <label>Field of Study</label>
                              <input
                                type="text"
                                value={edu.field}
                                onChange={(e) => updateEducation(index, 'field', e.target.value)}
                                placeholder="Mechanical Engineering"
                                className={validationErrors[`education_${index}_field`] ? 'error' : ''}
                                required
                              />
                              {validationErrors[`education_${index}_field`] && <span className="error-message">{validationErrors[`education_${index}_field`]}</span>}
                            </div>
                            <div className="form-group">
                              <label>Graduation Year</label>
                              <input
                                type="month"
                                value={edu.graduationYear}
                                onChange={(e) => updateEducation(index, 'graduationYear', e.target.value)}
                                className={validationErrors[`education_${index}_graduationYear`] ? 'error' : ''}
                                required
                              />
                              {validationErrors[`education_${index}_graduationYear`] && <span className="error-message">{validationErrors[`education_${index}_graduationYear`]}</span>}
                            </div>
                          </div>
                            <div className="form-row">
                              <div className="form-group">
                                <label>Start Year</label>
                                <input
                                  type="month"
                                  value={edu.startYear}
                                  onChange={(e) => updateEducation(index, 'startYear', e.target.value)}
                                  required
                                />
                              </div>
                              <div className="form-group">
                                <label>GPA</label>
                                <input
                                  type="text"
                                  value={edu.gpa}
                                  onChange={(e) => updateEducation(index, 'gpa', e.target.value)}
                                  placeholder="3.62/4.0"
                                />
                              </div>
                            </div>
                            <div className="form-row">
                              <div className="form-group">
                                <label>Location (State, Country)</label>
                                <input
                                  type="text"
                                  value={edu.location}
                                  onChange={(e) => updateEducation(index, 'location', e.target.value)}
                                  placeholder="Clemson, United States"
                                />
                              </div>
                            </div>
                        </div>
                      ))}
                      <button type="button" className="add-btn" onClick={addEducation}>
                        + Add Education
                      </button>

                      <h3 style={{ marginTop: '30px' }}>Skills</h3>
                      <div className="form-group">
                        <label>Technical & Soft Skills</label>
                        <div className="field-with-ai">
                          <textarea
                            value={skills}
                            onChange={(e) => {
                              setSkills(e.target.value);
                              // Clear validation error for skills
                              if (validationErrors.skills) {
                                setValidationErrors({ ...validationErrors, skills: null });
                              }
                            }}
                            placeholder="List your skills separated by commas (e.g., AutoCAD, SolidWorks, MATLAB, Project Management, Finite Element Analysis, etc.)"
                            rows="4"
                            className={validationErrors.skills ? 'error' : ''}
                            required
                          />
                          {validationErrors.skills && <span className="error-message">{validationErrors.skills}</span>}
                          <button 
                            type="button" 
                            className="ai-assist-btn"
                            onClick={handleAiSkills}
                            disabled={aiLoading}
                          >
                            {aiLoading && aiFieldType === 'skills' ? (
                              <>
                                <span className="ai-spinner">⟳</span> Generating...
                              </>
                            ) : (
                              <>
                                <span className="ai-icon">✨</span> AI Suggest
                              </>
                            )}
                          </button>
                        </div>
                      </div>

                      <h3 style={{ marginTop: '30px' }}>Languages</h3>
                      <div className="form-group">
                        <label>Specify your language proficiencies (optional)</label>
                        <div className="language-skills-container">
                          {languageSkills.map((langSkill, index) => (
                            <div key={index} className="language-skill-entry">
                              <div className="language-input-group">
                                <select
                                  value={langSkill.language}
                                  onChange={(e) => updateLanguageSkill(index, 'language', e.target.value)}
                                  className="language-select"
                                >
                                  <option value="">Select Language</option>
                                  <option value="english">English</option>
                                  <option value="spanish">Spanish</option>
                                  <option value="french">French</option>
                                  <option value="german">German</option>
                                  <option value="italian">Italian</option>
                                  <option value="portuguese">Portuguese</option>
                                  <option value="dutch">Dutch</option>
                                  <option value="russian">Russian</option>
                                  <option value="chinese">Chinese</option>
                                  <option value="japanese">Japanese</option>
                                  <option value="korean">Korean</option>
                                  <option value="arabic">Arabic</option>
                                  <option value="hindi">Hindi</option>
                                  <option value="bengali">Bengali</option>
                                  <option value="turkish">Turkish</option>
                                  <option value="vietnamese">Vietnamese</option>
                                  <option value="thai">Thai</option>
                                  <option value="swedish">Swedish</option>
                                  <option value="danish">Danish</option>
                                  <option value="norwegian">Norwegian</option>
                                  <option value="finnish">Finnish</option>
                                </select>
                                <select
                                  value={langSkill.proficiency}
                                  onChange={(e) => updateLanguageSkill(index, 'proficiency', e.target.value)}
                                  className="proficiency-select"
                                >
                                  <option value="">Proficiency</option>
                                  <option value="native">Native</option>
                                  <option value="fluent">Fluent</option>
                                  <option value="intermediate">Intermediate</option>
                                  <option value="basic">Basic</option>
                                </select>
                                <button
                                  type="button"
                                  className="remove-language-btn"
                                  onClick={() => removeLanguageSkill(index)}
                                  disabled={languageSkills.length === 1}
                                >
                                  ×
                                </button>
                              </div>
                            </div>
                          ))}
                          <button type="button" className="add-language-btn" onClick={addLanguageSkill}>
                            + Add Language
                          </button>
                        </div>
                      </div>

                      {/* Keep/Undo buttons for Step 3 - Only show if pre-filled from Job Tailored */}
                      {tailoredData && (
                        <div className="action-buttons">
                          <span className="action-message">
                            🎓 LeetEngineer prioritized your skills for maximum ATS impact. All set for your dream job?
                          </span>
                          <div className="action-buttons-group">
                            <button type="button" className="keep-btn" onClick={() => handleKeep(3)}>
                              <span className="btn-icon">✓</span> Keep
                            </button>
                            <button type="button" className="undo-btn" onClick={() => handleUndo(3)}>
                              <span className="btn-icon">↶</span> Undo
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Step 4: Review & Download */}
                  {currentStep === 4 && (
                    <div className="resume-step">
                      <h3>🎉 Review & Download Your Resume</h3>
                      <p className="step-description">
                        Review your resume preview below. Download when you're ready to share it with employers!
                      </p>

                      {/* Live Preview Panel */}
                      <div className="preview-scroll-container">
                        <ResumePreview
                          personalInfo={personalInfo}
                          professionalSummary={professionalSummary}
                          experience={experience}
                          education={education}
                          skills={skills}
                          languageSkills={languageSkills}
                          showDownloadButton={false}
                        />
                      </div>

                      {/* Download Actions */}
                      <div className="download-actions" style={{textAlign: 'center', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '15px'}}>
                        {pdfGenerating ? (
                          <div className="pdf-generating">
                            <div className="loading-spinner resume-spinner"></div>
                            <p className="loading-text-resume">Generating your professional resume PDF...</p>
                          </div>
                        ) : (
                          <>
                            <button
                              type="button"
                              className="nav-btn prev-btn"
                              onClick={prevStep}
                            >
                              Previous
                            </button>
                            <button
                              type="button"
                              className="download-btn"
                              onClick={async () => {
                              setPdfGenerating(true);
                              try {
                                // Create a clean, isolated container for PDF generation
                                const tempContainer = document.createElement('div');
                                tempContainer.style.position = 'fixed';
                                tempContainer.style.left = '0';
                                tempContainer.style.top = '0';
                                tempContainer.style.width = '794px'; // A4 width at 96 DPI
                                tempContainer.style.zIndex = '-9999';
                                tempContainer.style.visibility = 'hidden';
                                tempContainer.style.overflow = 'visible';
                                document.body.appendChild(tempContainer);

                                // Use ReactDOM to render the ResumePreview component
                                const { createRoot } = await import('react-dom/client');
                                const root = createRoot(tempContainer);
                                
                                // Render the resume preview directly without extra wrapper
                                await new Promise((resolve) => {
                                  root.render(
                                    <ResumePreview 
                                      personalInfo={personalInfo}
                                      professionalSummary={professionalSummary}
                                      experience={experience}
                                      education={education}
                                      skills={skills}
                                      languageSkills={languageSkills}
                                      showDownloadButton={false}
                                    />
                                  );
                                  // Give React time to fully render and apply styles
                                  setTimeout(resolve, 500);
                                });

                                // Get the actual resume content (traditional-template)
                                const resumeElement = tempContainer.querySelector('.traditional-template');

                                if (!resumeElement) {
                                  alert('Failed to render resume for download');
                                  document.body.removeChild(tempContainer);
                                  setPdfGenerating(false);
                                  return;
                                }

                                // Force white background and proper sizing for reproducibility
                                resumeElement.style.backgroundColor = '#ffffff';
                                resumeElement.style.width = '794px';
                                resumeElement.style.minHeight = 'auto';
                                resumeElement.style.margin = '0';
                                resumeElement.style.padding = '15mm';
                                resumeElement.style.boxSizing = 'border-box';
                                resumeElement.style.overflow = 'visible';

                                const opt = {
                                  margin: 0, // No margins - content controls its own spacing
                                  filename: `${personalInfo?.fullName || 'Resume'}_Resume.pdf`,
                                  image: { 
                                    type: 'jpeg', 
                                    quality: 0.98 
                                  },
                                  html2canvas: { 
                                    scale: 2,
                                    useCORS: true,
                                    letterRendering: true,
                                    logging: false,
                                    backgroundColor: '#ffffff',
                                    windowWidth: 794,
                                    scrollX: 0,
                                    scrollY: 0,
                                    timeout: 500
                                  },
                                  jsPDF: { 
                                    unit: 'mm', 
                                    format: 'a4', 
                                    orientation: 'portrait',
                                    compress: true
                                  },
                                  pagebreak: { 
                                    mode: ['css', 'legacy'],
                                    before: '.page-break-before',
                                    after: '.page-break-after',
                                    avoid: '.avoid-break'
                                  }
                                };

                                // Generate and download PDF
                                try {
                                  await html2pdf().set(opt).from(resumeElement).save();
                                } catch (error) {
                                  console.error('PDF generation error:', error);
                                  alert('Failed to generate PDF. Please try again.');
                                } finally {
                                  // Clean up
                                  root.unmount();
                                  document.body.removeChild(tempContainer);
                                }
                              } catch (error) {
                                console.error('Resume download failed:', error);
                                alert('Failed to download resume. Please try again.');
                              } finally {
                                setPdfGenerating(false);
                              }
                            }}
                            style={{display: 'block', margin: '0 auto'}}
                          >
                            📥 Download Resume PDF
                          </button>
                          </>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Navigation Buttons */}
                  <div className="resume-navigation">
                    {currentStep > 1 && currentStep < 4 && (
                      <button type="button" className="nav-btn prev-btn" onClick={prevStep}>
                        Previous
                      </button>
                    )}
                    {currentStep < 3 ? (
                      <button type="button" className="nav-btn next-btn" onClick={nextStep}>
                        Next
                      </button>
                    ) : currentStep === 3 ? (
                      <button type="submit" className="nav-btn next-btn generate-resume-btn">
                        Generate AI Resume
                      </button>
                    ) : null}
                  </div>
                </form>
            </div>
          </div>
        ) : (
          <JobTailoredResumeForm
            jobUrl={jobUrl}
            setJobUrl={setJobUrl}
            resumeFile={resumeFile}
            handleFileChange={handleFileChange}
            handleSubmit={handleSubmit}
            isLoading={isLoading}
          />
        )}
        <Feedback />
      </div>
      <div className="dashboard-footer">
        <Footer />
      </div>

      {/* Session Conflict Modal */}
      {showSessionConflictModal && (
        <div className="session-conflict-modal-overlay">
          <div className="session-conflict-modal">
            <div className="session-conflict-modal-header">
              <h3>Resume Session Detected</h3>
              <p>You have an incomplete job-tailored resume session. What would you like to do?</p>
            </div>
            <div className="session-conflict-modal-body">
              <div className="session-info">
                <div className="session-details">
                  <span className="session-label">Current Progress:</span>
                  <span className="session-value">
                    Step {Math.min(currentStep || 0, 3)} of 4
                    {currentStep === 1 && " (Personal Information)"}
                    {currentStep === 2 && " (Experience)"}
                    {currentStep === 3 && " (Education & Skills)"}
                    {currentStep >= 4 && " (Complete)"}
                  </span>
                </div>
                {tailoredData?.personalInfo?.fullName && (
                  <div className="session-details">
                    <span className="session-label">Resume For:</span>
                    <span className="session-value">{tailoredData.personalInfo.fullName}</span>
                  </div>
                )}
              </div>
            </div>
            <div className="session-conflict-modal-actions">
              <button
                className="session-action-btn session-continue-btn"
                onClick={handleContinueSession}
              >
                <span className="btn-icon">▶️</span>
                Continue Existing Session
              </button>
              <button
                className="session-action-btn session-new-btn"
                onClick={handleStartNewSession}
              >
                <span className="btn-icon">🆕</span>
                Start New Resume
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default JobTailoredResume;
